import {makeAutoObservable, runInAction} from 'mobx';
import createAxiosInstance from '../support/AxiosSupport';
import logger from '../utils/logger';

/**
 * Уведомление центра уведомлений (контракт CTHS-42, REST + WS). Совпадает с backend
 * {@code NotificationDto}: транзиентные WS-тосты прошлого поколения несли только
 * {@code title/text/type} — теперь WS пушит полный объект с {@code id}, что и позволяет
 * дедуп live-события против уже сохранённого уведомления.
 */
export interface NotificationItem {
    id: string;
    type: string;
    title: string;
    text: string;
    read: boolean;
    createdAt: string;
    refType?: string | null;
    refId?: string | null;
    deepLink?: string | null;
}

/** Нормализованная страница (подмножество Spring `Page`), используемая страницей «Все уведомления». */
export interface NotificationPage {
    content: NotificationItem[];
    number: number;
    totalPages: number;
    totalElements: number;
    first: boolean;
    last: boolean;
}

/** Сколько последних уведомлений держим в выпадающем списке колокольчика. */
export const RECENT_LIMIT = 8;

/**
 * MobX-стор центра уведомлений. Единый источник истины для badge (счётчик непрочитанных)
 * и выпадающего списка; и REST, и live WS-события сводятся сюда.
 *
 * Реконсиляция (AC3): badge и список берутся с сервера (REST) — переживают перезагрузку и
 * переподключение WS без рассинхрона; входящее WS-событие лишь инкрементально дополняет их
 * с дедупом по {@code id}, чтобы не задвоить уже сохранённое уведомление.
 */
class NotificationStore {
    /** Счётчик непрочитанных для badge (AC1). Берётся с сервера, переживает reload. */
    unreadCount = 0;
    /** Последние N уведомлений для выпадающего списка. */
    recent: NotificationItem[] = [];
    loadingRecent = false;
    recentError = false;

    private initialized = false;

    constructor() {
        makeAutoObservable(this);
    }

    /** AC1: badge = число непрочитанных, берётся с сервера. Ошибка не роняет badge — оставляем прежнее. */
    loadUnreadCount = async (): Promise<void> => {
        try {
            const axios = await createAxiosInstance();
            const {data} = await axios.get('/notifications/unread-count');
            runInAction(() => {
                this.unreadCount = Math.max(0, Number(data?.count ?? 0) || 0);
            });
        } catch (e) {
            logger.debug('NotificationStore: loadUnreadCount failed', {error: (e as Error)?.message});
        }
    };

    /** Список последних уведомлений для выпадашки. Управляет состояниями loading/empty/error (AC3). */
    loadRecent = async (): Promise<void> => {
        runInAction(() => {
            this.loadingRecent = true;
            this.recentError = false;
        });
        try {
            const axios = await createAxiosInstance();
            const {data} = await axios.get('/notifications', {params: {page: 0, size: RECENT_LIMIT}});
            runInAction(() => {
                this.recent = Array.isArray(data?.content) ? data.content : [];
                this.loadingRecent = false;
            });
        } catch (e) {
            logger.debug('NotificationStore: loadRecent failed', {error: (e as Error)?.message});
            runInAction(() => {
                this.recentError = true;
                this.loadingRecent = false;
            });
        }
    };

    /**
     * Полная страница для маршрута «Все уведомления». Возвращает данные, а не пишет в observable-
     * стейт: пагинированный список живёт в локальном стейте страницы, чтобы не конфликтовать с
     * live-дополнением выпадашки.
     */
    fetchPage = async (page: number, size: number): Promise<NotificationPage> => {
        const axios = await createAxiosInstance();
        const {data} = await axios.get('/notifications', {params: {page, size}});
        return {
            content: Array.isArray(data?.content) ? data.content : [],
            number: Number(data?.number ?? page) || 0,
            totalPages: Number(data?.totalPages ?? 0) || 0,
            totalElements: Number(data?.totalElements ?? 0) || 0,
            first: data?.first ?? page === 0,
            last: data?.last ?? true,
        };
    };

    /** AC2: пометить прочитанным и уменьшить badge. No-op для уже прочитанного (идемпотентно). */
    markRead = async (item: NotificationItem): Promise<void> => {
        if (!item?.id || item.read) {
            return;
        }
        try {
            const axios = await createAxiosInstance();
            await axios.post(`/notifications/${item.id}/read`);
            runInAction(() => {
                const it = this.recent.find(n => n.id === item.id);
                if (it) {
                    it.read = true;
                }
                this.unreadCount = Math.max(0, this.unreadCount - 1);
            });
        } catch (e) {
            logger.debug('NotificationStore: markRead failed', {error: (e as Error)?.message});
        }
    };

    /** AC2: «прочитать все» очищает счётчик (сервер возвращает count=0). */
    markAllRead = async (): Promise<void> => {
        try {
            const axios = await createAxiosInstance();
            const {data} = await axios.post('/notifications/read-all');
            runInAction(() => {
                this.recent.forEach(n => {
                    n.read = true;
                });
                this.unreadCount = Math.max(0, Number(data?.count ?? 0) || 0);
            });
        } catch (e) {
            logger.debug('NotificationStore: markAllRead failed', {error: (e as Error)?.message});
        }
    };

    /**
     * AC3: применить live WS-событие. Дедуп по {@code id} — уже присутствующее уведомление не
     * вставляется повторно и не двоит badge. Уведомления без {@code id} (legacy-формат WS) в
     * списке не учитываются (тост по-прежнему показывается в NotificationService).
     */
    ingestLive = (dto: NotificationItem): void => {
        if (!dto?.id) {
            return;
        }
        if (this.recent.some(n => n.id === dto.id)) {
            return;
        }
        this.recent = [dto, ...this.recent].slice(0, RECENT_LIMIT);
        if (!dto.read) {
            this.unreadCount += 1;
        }
    };

    /** Инициализация при первом монтировании колокольчика у аутентифицированного пользователя. */
    init = async (): Promise<void> => {
        if (this.initialized) {
            return;
        }
        this.initialized = true;
        await this.loadUnreadCount();
    };

    /** Сброс при логауте/смене пользователя. */
    reset = (): void => {
        runInAction(() => {
            this.unreadCount = 0;
            this.recent = [];
            this.initialized = false;
        });
    };
}

const notificationStore = new NotificationStore();

export default notificationStore;
